import { useCallback, useState } from "react";
import { DocumentNode, LazyQueryHookOptions, useLazyQuery } from "@apollo/client";
import { debounce } from "lodash";

function useSearchAndSelect<T>(query: DocumentNode, options?: LazyQueryHookOptions) {
  const [searchText, setSearchText] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [showNoResultsFound, setShowNoResultsFound] = useState(false);
  const [getItemsQuery, { loading }] = useLazyQuery(query, options);

  const fetchItems = async (term: string) => {
    const { data } = await getItemsQuery({ variables: { term } });
    const fetchedItems = data?.[Object.keys(data)[0]] || [];
    setShowNoResultsFound(fetchedItems.length === 0);
    setItems(fetchedItems);
  };

  const debouncedFetch = useCallback(debounce(fetchItems, 300), [fetchItems]);

  const onSearchTextChange = useCallback(
    (text: string) => {
      setSearchText(text);
      if (text) {
        debouncedFetch(text);
      } else {
        setItems([]);
      }
    },
    [setSearchText, setItems, debouncedFetch]
  );

  return { searchText, onSearchTextChange, items, loading, showNoResultsFound };
}

export default useSearchAndSelect;
